import { Box, Button } from '@superdispatch/ui-lab';
import { useEffect } from 'react';
import { useDocumentTitle } from 'shared/layout/DocumentTitle';
import { PermissionView } from 'shared/modules/permission/core/PermissionView';
import { SuperPayBankAccountDTO } from 'shared/modules/superpay/SuperPayDTO';
import { useBypassNavigationPrompt } from 'shared/routing/NavigationBlock';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { useSuperPaySettings } from '../../../profile/data/SuperPayAPI';
import { useTrackChangeBankAccountEvent } from '../../../profile/superpay/account-details/ChangeBankAccountAnalytics';
import { useChangeBankAccountBankInfoForm } from '../../../profile/superpay/account-details/ChangeBankAccountBankInfoStep';
import { useChangeBankAccountConfirmForm } from '../../../profile/superpay/account-details/ChangeBankAccountConfirmStep';
import { ChangeBankAccountForm } from '../../../profile/superpay/account-details/ChangeBankAccountForm';
import {
  BankAccountVerificationResult,
  SubmittedBankAccount,
} from '../../../profile/superpay/account-details/ChangeBankAccountTypes';
import { VerificationCodeForm } from 'shared/form/VerificationCodeForm';
import { useChangeBankAccountFlow } from '../../../profile/superpay/account-details/useChangeBankAccountFlow';
import { SUPERPAY_WEBVIEW_ROUTES } from '../../../profile/superpay/SuperPayRoute';
import { BankAccountVerificationWebViewStep } from './BankAccountVerificationWebViewStep';
import { MobileDrawerLayout } from './MobileDrawerLayout';
import { SuperPayWebviewHeader } from './SuperPayWebviewHeader';
import { useInterceptBackButton } from './useInterceptBackButton';

function BankInfoStep({
  bankAccount,
  onContinue,
}: {
  bankAccount?: SuperPayBankAccountDTO;
  onContinue: (bankAccount: SubmittedBankAccount) => void;
}) {
  const formik = useChangeBankAccountBankInfoForm({ onContinue });

  return (
    <MobileDrawerLayout
      formik={formik}
      title="Change Bank Account"
      action={
        <Button
          onClick={() => {
            void formik.submitForm();
          }}
          pending={formik.isSubmitting}
          fullWidth={true}
        >
          Continue
        </Button>
      }
    >
      <ChangeBankAccountForm bankAccount={bankAccount} />
    </MobileDrawerLayout>
  );
}

function ConfirmStep({
  bankAccount,
  onComplete,
}: {
  bankAccount: SubmittedBankAccount;
  onComplete: (result: BankAccountVerificationResult) => void;
}) {
  const {
    formik,
    sendCode,
    resendAvailableAt,
    verificationMethod,
    recipient,
    onSendEmail,
    onSendTextMessage,
  } = useChangeBankAccountConfirmForm({ bankAccount, onComplete });
  const isSubmitDisabled = !formik.dirty || !formik.isValid;

  return (
    <MobileDrawerLayout
      formik={formik}
      title="Change Bank Account"
      action={
        <Button
          onClick={() => {
            void formik.submitForm();
          }}
          pending={formik.isSubmitting}
          disabled={isSubmitDisabled}
          fullWidth={true}
        >
          Continue
        </Button>
      }
    >
      <VerificationCodeForm
        recipient={recipient}
        verificationMethod={verificationMethod}
        resendAvailableAt={resendAvailableAt}
        onResend={sendCode}
        onSendEmail={onSendEmail}
        onSendTextMessage={onSendTextMessage}
        hideSubmitButton={true}
      />
    </MobileDrawerLayout>
  );
}

export function ChangeBankAccountWebViewPage() {
  const navigate = useBypassNavigationPrompt();
  const { refetch: refetchCarrierSettings } = useCarrierSettings();
  const { data: superPaySettings, refetch: refetchSuperPaySettings } =
    useSuperPaySettings();
  const trackChangeBankAccountEvent = useTrackChangeBankAccountEvent();

  useDocumentTitle('SuperPay');

  useEffect(() => {
    trackChangeBankAccountEvent('Carrier Opened Change Bank Account Slider');
  }, [trackChangeBankAccountEvent]);

  const onComplete = () => {
    void refetchCarrierSettings();
    void refetchSuperPaySettings();
    navigate(
      `${SUPERPAY_WEBVIEW_ROUTES.accountDetails}?utm_medium=Change Bank Account Drawer`,
      { replace: true },
    );
  };

  useInterceptBackButton();

  const { step, submittedBankAccount, handleContinue, handleConfirmed } =
    useChangeBankAccountFlow({ onComplete });

  return (
    <SuperPayWebviewHeader>
      <PermissionView name="superpay" permission="can_edit">
        <Box width="100%" maxWidth="496px" marginLeft="auto" marginRight="auto">
          {step === 'verify' ? (
            <BankAccountVerificationWebViewStep
              isTitleOnTop={true}
              bankAccount={superPaySettings?.bank_accounts?.[0]}
              onComplete={onComplete}
            />
          ) : step === 'confirm' && submittedBankAccount ? (
            <ConfirmStep
              bankAccount={submittedBankAccount}
              onComplete={handleConfirmed}
            />
          ) : (
            <BankInfoStep
              bankAccount={superPaySettings?.bank_accounts?.[0]}
              onContinue={handleContinue}
            />
          )}
        </Box>
      </PermissionView>
    </SuperPayWebviewHeader>
  );
}
